import type { ResponsiveMarginProps } from '@withjoy/joykit/styled-system';
import React from 'react';
import { GoogleCalendar, OutlookCalendar } from 'datebook';
import { Menu } from '@withjoy/joykit/components/Menu';
import { Box, DrawerV2, PopoverV2, SpacingStack } from '@withjoy/joykit';
import { useDisclosure } from '@withjoy/joykit';
import { BrandGoogle, BrandOutlook, BrandApple } from '@withjoy/joykit/icons';
import { ReactComponent as BrandOutlookInverse } from '@assets/icons/brand-outlook-inverse.svg';
import { ReactComponent as BrandAppleInverse } from '@assets/icons/brand-apple-inverse.svg';
import { useAddToCalendarTranslations } from './AddToCalendar.i18n';
import { StyledMenuItem, menuItemOverrides, styles, dialogOverridesMobile, DrawerItem, CloseItem } from './AddToCalendar.styles';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { withWindow } from '@shared/utils/withWindow';
import { buildCalendarIcsUrl, resolveCalendarDates } from './AddToCalendar.utils';
import { useEventInfo } from '@shared/utils/eventInfo';

export type AddToCalendarProps = Readonly<{
  title?: string;
  description?: string;
  location?: string;
  start: string;
  end?: string;
  children: React.ReactElement | React.ReactText;
  handleClickAddToCalendar?: (title: string, type: string) => void;
  allDay?: boolean;
  /** Overrides the event read from context. Ecard and save the date previews render a specific
   *  event rather than the one the surrounding page is about, so they pass their own. Everywhere
   *  else this is the context event and does not need threading through the tree. */
  eventId?: string;
  /** The schedule item, where the button is for one. Gives the calendar entry a stable
   *  identity, so a guest who adds the same item twice ends up with one entry, not two. */
  scheduleItemId?: string;
}> &
  ResponsiveMarginProps;

const AddToCalendar: React.FC<AddToCalendarProps> = ({ allDay, children, title, location, description, start, end, eventId, scheduleItemId, handleClickAddToCalendar }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { google, apple, outlook, cancel } = useAddToCalendarTranslations();
  // `useEventInfo` is safe outside a provider — it returns `{ loading: false }` rather than
  // throwing — and the two arms are mutually exclusive, so both are needed. The id is `Maybe` at
  // its source and the route treats it that way, so an absent one degrades attribution rather
  // than breaking the download.
  const { eventInfo, ssrEventInfo } = useEventInfo();
  const resolvedEventId = eventId ?? eventInfo?.eventId ?? ssrEventInfo?.eventId ?? undefined;
  const { dateStart, dateEnd, icsEnd, isAllDay } = resolveCalendarDates({ start, end, allDay });
  const config = {
    title,
    location,
    description,
    start: dateStart,
    end: dateEnd,
  };
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const mobileOpen = (isMobile ?? false) && isOpen;
  const nonMobileOpen = !isMobile && isOpen;

  const handleClick = (type: 'google' | 'apple' | 'outlook') => () => {
    switch (type) {
      case 'google': {
        const googleCalendar = new GoogleCalendar(config);
        const calendarUrl = googleCalendar.render();
        withWindow(global => global.open(calendarUrl, '_blank'));
        break;
      }
      case 'apple': {
        // Apple needs a file where the other two take a hosted URL, and a file the browser builds
        // for itself never arrives on iOS — WebKit ignores the `download` attribute datebook's
        // `ICalendar.download()` depends on, so the tap did nothing at all. Asking the server for
        // the same bytes makes this the same gesture as Google and Outlook: open a link.
        const calendarUrl = buildCalendarIcsUrl({
          eventId: resolvedEventId,
          scheduleItemId,
          provider: 'apple',
          title,
          start: dateStart,
          end: icsEnd,
          allDay: isAllDay,
          location,
          description,
        });
        withWindow(global => global.open(calendarUrl, '_blank'));
        break;
      }
      default: {
        const outlookCalendar = new OutlookCalendar(config);
        const calendarUrl = outlookCalendar.render();
        withWindow(global => global.open(calendarUrl, '_blank'));
        break;
      }
    }
    if (handleClickAddToCalendar) {
      handleClickAddToCalendar(title || '', type);
    }
    onClose();
  };

  return (
    <PopoverV2
      triggerType="click"
      isOpen={nonMobileOpen}
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverV2.Trigger>{children}</PopoverV2.Trigger>
      <PopoverV2.Content>
        <Menu>
          <StyledMenuItem
            __css={styles.menuitem}
            overrides={menuItemOverrides}
            data-testid="menuitem-google"
            content={
              <Box
                as="button"
                __css={styles.button}
                onClick={handleClick('google')}
              >
                <BrandGoogle /> {google}
              </Box>
            }
          />
          <StyledMenuItem
            __css={styles.menuitem}
            overrides={menuItemOverrides}
            data-testid="menuitem-apple"
            content={
              <Box
                as="button"
                __css={styles.button}
                onClick={handleClick('apple')}
              >
                <BrandAppleInverse /> {apple}
              </Box>
            }
          />
          <StyledMenuItem
            __css={styles.menuitem}
            overrides={menuItemOverrides}
            data-testid="menuitem-outlook"
            content={
              <Box
                as="button"
                __css={styles.button}
                onClick={handleClick('outlook')}
              >
                <BrandOutlookInverse /> {outlook}
              </Box>
            }
          />
        </Menu>
      </PopoverV2.Content>
      <DrawerV2
        isOpen={mobileOpen}
        onClose={onClose}
        enableDividers={true}
        anchor="bottom"
        overrides={dialogOverridesMobile}
        size="sm"
      >
        <DrawerV2.Header
          typographyVariant="hed4"
          textAlign="center"
        >
          {title}
        </DrawerV2.Header>
        <DrawerV2.Body>
          <SpacingStack
            spacing={2}
            as="ul"
            alignItems={'center'}
          >
            <DrawerItem onClick={handleClick('google')}>
              <BrandGoogle /> {google}
            </DrawerItem>
            <DrawerItem onClick={handleClick('apple')}>
              <BrandApple /> {apple}
            </DrawerItem>
            <DrawerItem onClick={handleClick('outlook')}>
              <BrandOutlook /> {outlook}
            </DrawerItem>
            <CloseItem onClick={onClose}>{cancel}</CloseItem>
          </SpacingStack>
        </DrawerV2.Body>
      </DrawerV2>
    </PopoverV2>
  );
};

AddToCalendar.displayName = 'AddToCalendar';

export { AddToCalendar };
